import NewCheckbox from 'client/components/core/Sidebar/components/Settings/components/NewCheckbox'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import { useAppDispatch } from 'client/hooks/use-dispatch'
import { getRootEntity } from 'client/reducers/pageReducer'
import { usePage } from 'client/store'
import { updateEntity } from 'client/store/page/pageActions'
import { MAX_CLOSED_POPUP_DAYS } from 'common/constants/popup'
import { RemotePopupBodyInterface } from 'common/types/remote-popup-body-interface'
import { getMobileBackgroundFileId } from 'common/utils/getMobileBackgroundFileId'
import React from 'react'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { CheckboxControl } from 'src/editor/shared/settings/checkbox-control'
import { ColorPickerControl } from 'src/editor/shared/settings/controls/color-picker/color-picker-control'
import { ImageControl } from 'src/editor/shared/settings/controls/image-control'
import { SizeAndPositionGroup } from 'src/editor/shared/settings/groups/size-and-position'
import { RangeElement } from 'src/editor/shared/ui-lib/range-element'
import {
  OptionSingleValueType,
  SelectNew,
} from 'src/editor/shared/ui-lib/select-new/select-new'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { useUpdateProp } from '../hooks/useUpdateProps'
import { LanguageSelector } from '../options'
import { ShadowStyle } from '../styles'
import Border from '../styles/Border'
import { Typography } from './components/typography-new'

export const RemotePopupFormSettings = () => {
  // @description: for RemotePopupBody
  const { t } = useTypedTranslation()
  const entity: RemotePopupBodyInterface = usePage(getRootEntity)

  const updateProp = useUpdateProp(entity)
  const { isMobile } = useDeviceModeContext()

  const dispatch = useAppDispatch()

  const selectOptions = [
    { value: true, label: t('ON') },
    { value: false, label: t('OFF') },
  ]

  const toggleOpenOnExit = (option: OptionSingleValueType<boolean>) => {
    const updatedEntity = { ...entity }
    if (!option?.value) {
      updatedEntity.openOnMobileAutomatically = false
      // biome-ignore lint/performance/noDelete: <explanation>
      delete updatedEntity.mobileDelay
    }
    updatedEntity.openOnExit = !!option?.value
    dispatch(
      updateEntity({
        ...updatedEntity,
      }),
    )
  }

  const toggleDelayOption = (value: boolean) => {
    const updatedEntity = { ...entity }
    if (value) {
      updatedEntity.closedDays = 1
    } else {
      // biome-ignore lint/performance/noDelete: <explanation>
      delete updatedEntity.closedDays
    }
    dispatch(updateEntity(updatedEntity))
  }

  return (
    <>
      <SelectNew
        labelText={'show close button'}
        options={selectOptions}
        onChange={option => updateProp('showClose')(!!option?.value)}
        isMulti={false}
        selectedOption={entity.showClose ? selectOptions[0] : selectOptions[1]}
      />
      {entity.showClose && (
        <>
          <ColorPickerControl
            label={t('entity_settings.popup.color_button_close')}
            update={updateProp('closeButtonColor')}
            mobileUpdate={updateProp('mobileCloseButtonColor')}
            color={entity.closeButtonColor}
            mobileColor={entity.mobileCloseButtonColor}
            isClearable
          />
          <NewCheckbox
            checked={!!entity.closedDays}
            update={toggleDelayOption}
            labelText="entity_settings.popup.delay.label"
            helpMessage="entity_settings.popup.delay.help_message"
          />
        </>
      )}
      {entity.showClose && !!entity.closedDays && (
        <RangeElement
          label={t('entity_settings.popup.reopen_in_x_days_icu', {
            days: entity.closedDays || 0,
          })}
          onChange={updateProp('closedDays')}
          value={entity.closedDays}
          min={1}
          max={MAX_CLOSED_POPUP_DAYS}
          allowNegative={false}
        />
      )}
      <SelectNew
        options={selectOptions}
        selectedOption={
          entity.openAutomatically ? selectOptions[0] : selectOptions[1]
        }
        onChange={option => {
          updateProp('openAutomatically')(!!option?.value)
        }}
        labelText="entity_settings.popup.open_automatically"
        isMulti={false}
      />
      {entity.openAutomatically && (
        <RangeElement
          label={t('entity_settings.popup.open_in_x_seconds_icu', {
            seconds: entity.delay,
          })}
          value={entity.delay}
          onChange={updateProp('delay')}
          min={0}
          max={120}
        />
      )}
      <SelectNew
        selectedOption={entity.openOnExit ? selectOptions[0] : selectOptions[1]}
        options={selectOptions}
        onChange={toggleOpenOnExit}
        labelText="entity_settings.popup.open_on_exit"
        isMulti={false}
      />
      {entity.openOnExit && (
        <CheckboxControl
          label="entity_settings.popup.mobile_open_on_exit"
          value={entity.openOnMobileAutomatically}
          update={updateProp('openOnMobileAutomatically')}
        />
      )}
      {entity.openOnMobileAutomatically && (
        <RangeElement
          label={t('entity_settings.popup.open_in_x_seconds_icu', {
            seconds: entity.mobileDelay,
          })}
          value={entity.mobileDelay}
          onChange={(val: number) => val > 0 && updateProp('mobileDelay')(val)}
          min={0}
          max={120}
        />
      )}
      <Typography />
      <GroupTitle>page_settings.body.background.group_title</GroupTitle>
      <ColorPickerControl
        label="settings_styles.background_color.label"
        update={updateProp('backgroundColor')}
        mobileUpdate={updateProp('mobileBackgroundColor')}
        color={entity.backgroundColor}
        mobileColor={entity.mobileBackgroundColor}
      />
      <ImageControl
        isMobile={isMobile}
        backgroundPosition={
          isMobile
            ? entity.mobileBackground || entity.background
            : entity.background
        }
        fileId={
          isMobile
            ? getMobileBackgroundFileId(
                entity.backgroundFileId,
                entity.mobileBackgroundFileId,
              )
            : entity.backgroundFileId
        }
        updateBackgroundFileId={
          isMobile
            ? updateProp('mobileBackgroundFileId')
            : updateProp('backgroundFileId')
        }
        update={
          isMobile ? updateProp('mobileBackground') : updateProp('background')
        }
      />
      <RangeElement
        label="page_settings.body.background_blur.label"
        value={entity.blur}
        onChange={updateProp('blur')}
        max={10}
      />
      <SizeAndPositionGroup
        padding={isMobile ? entity?.mobilePadding : entity.padding}
        updatePadding={updateProp(isMobile ? 'mobilePadding' : 'padding')}
      />
      <Border
        border={isMobile ? entity.mobileBorder : entity.border}
        update={isMobile ? updateProp('mobileBorder') : updateProp('border')}
      />
      <ShadowStyle
        shadow={entity.boxShadow}
        mobileShadow={entity.mobileBoxShadow}
        update={updateProp('boxShadow')}
        mobileUpdate={updateProp('mobileBoxShadow')}
      />
      <LanguageSelector />
    </>
  )
}
